import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from '../lib/i18n';
import { useAuthStore } from '../lib/auth';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { toast } from 'sonner';
import usePasteUpload from '../hooks/usePasteUpload';
import { Money } from '../components/Money';
import {
  Fuel, Upload, Plus, Pencil, Trash2, Search, FileSpreadsheet, X, Download,
  ChevronsUpDown, Check, Loader2, Paperclip, Copy, FileText, Image as ImageIcon, Eye, Save,
  ZoomIn, ZoomOut, RotateCw, ChevronLeft, ChevronRight
} from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const API_URL = process.env.REACT_APP_BACKEND_URL;

const normalizeTurkish = (str) => {
  if (!str) return '';
  return str.toLowerCase()
    .replace(/ı/g, 'i').replace(/İ/g, 'i')
    .replace(/ğ/g, 'g').replace(/Ğ/g, 'g')
    .replace(/ü/g, 'u').replace(/Ü/g, 'u')
    .replace(/ş/g, 's').replace(/Ş/g, 's')
    .replace(/ö/g, 'o').replace(/Ö/g, 'o')
    .replace(/ç/g, 'c').replace(/Ç/g, 'c');
};

const formatCurrency = (val) => {
  const num = parseFloat(val) || 0;
  return new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(num);
};

const emptyForm = {
  firm_id: '', date: '', license_plate: '', tank_card: '',
  driver_name: '', driver_id: '', liters: '', km: '',
  price_per_liter: '', total_cost: '', period: '',
  service_partner: '', notes: ''
};

export default function FuelReports() {
  const { t } = useTranslation();
  const { user, accessToken } = useAuthStore();

  const [reports, setReports] = useState([]);
  // Sayfalama — büyük yakıt raporu listelerinde gezinmeyi hızlandırır.
  // Sayfa boyutu localStorage'a kaydedilir → kullanıcının tercihi korunur.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    const v = parseInt(localStorage.getItem('fuel_reports_page_size') || '50', 10);
    return [25, 50, 100, 200].includes(v) ? v : 50;
  });
  const [loading, setLoading] = useState(false);
  const [firms, setFirms] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [periods, setPeriods] = useState([]);

  const [filterFirm, setFilterFirm] = useState('');
  const [filterPeriod, setFilterPeriod] = useState('');
  const [filterDateStart, setFilterDateStart] = useState('');
  const [filterDateEnd, setFilterDateEnd] = useState('');
  const [searchText, setSearchText] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [driverComboOpen, setDriverComboOpen] = useState(false);
  const [showAllDrivers, setShowAllDrivers] = useState(false);
  const [loadingAll, setLoadingAll] = useState(false);

  const [showImport, setShowImport] = useState(false);
  const [importFirmId, setImportFirmId] = useState('');
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);

  const [plateComboOpen, setPlateComboOpen] = useState(false);

  // Attachments
  const [showAttach, setShowAttach] = useState(false);
  const [attachReportId, setAttachReportId] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Form inline attachments (drag & drop)
  const [pendingFiles, setPendingFiles] = useState([]);
  const [formAttachments, setFormAttachments] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [savingForm, setSavingForm] = useState(false);
  
  // Attachments dialog drag state
  const [attachDragging, setAttachDragging] = useState(false);

  // File preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pdfPageNumber, setPdfPageNumber] = useState(1);
  const [pdfNumPages, setPdfNumPages] = useState(null);

  // File type helpers
  const isImageFile = (f) => f?.content_type?.toLowerCase()?.startsWith('image/') || f?.is_image;
  const isPdfFile = (f) => f?.content_type?.toLowerCase() === 'application/pdf' || f?.filename?.toLowerCase()?.endsWith('.pdf');
  const isPreviewable = (f) => isImageFile(f) || isPdfFile(f);

  const headers = useMemo(() => ({
    'Authorization': `Bearer ${accessToken}`,
    'Content-Type': 'application/json'
  }), [accessToken]);

  const fetchFirms = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/firms`, { headers });
      const data = await res.json();
      if (data.success || data.data) setFirms(data.data || []);
    } catch {}
  }, [headers]);

  const fetchEmployees = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/fuel-reports/drivers`, { headers });
      const data = await res.json();
      if (data.data) {
        const emps = data.data.employees || data.data || [];
        setEmployees(emps);
      }
    } catch {}
  }, [headers]);

  const fetchAllEmployees = useCallback(async () => {
    setLoadingAll(true);
    try {
      const res = await fetch(`${API_URL}/api/fuel-reports/drivers`, { headers });
      const data = await res.json();
      if (data.data) {
        const emps = data.data.employees || data.data || [];
        setAllEmployees(emps);
      }
    } catch {}
    setLoadingAll(false);
  }, [headers]);

  const fetchVehicles = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/vehicles`, { headers });
      const data = await res.json();
      if (data.data) setVehicles(data.data || []);
    } catch {}
  }, [headers]);

  const fetchPeriods = useCallback(async () => {
    try {
      const q = (filterFirm && filterFirm !== 'all') ? `?firm_id=${filterFirm}` : '';
      const res = await fetch(`${API_URL}/api/fuel-reports/periods${q}`, { headers });
      const data = await res.json();
      if (data.data) setPeriods(data.data || []);
    } catch {}
  }, [headers, filterFirm]);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterFirm && filterFirm !== 'all') params.set('firm_id', filterFirm);
      if (filterPeriod && filterPeriod !== 'all') params.set('period', filterPeriod);
      if (filterDateStart) params.set('start_date', filterDateStart);
      if (filterDateEnd) params.set('end_date', filterDateEnd);
      const res = await fetch(`${API_URL}/api/fuel-reports?${params}`, { headers });
      const data = await res.json();
      if (data.success) setReports(data.data || []);
    } catch { toast.error(t('fuelReports.loadError')); }
    finally { setLoading(false); }
  }, [headers, filterFirm, filterPeriod, filterDateStart, filterDateEnd]);

  useEffect(() => { fetchFirms(); fetchEmployees(); fetchVehicles(); }, [fetchFirms, fetchEmployees, fetchVehicles]);
  useEffect(() => { fetchPeriods(); }, [fetchPeriods]);
  useEffect(() => { fetchReports(); }, [fetchReports]);

  const filteredReports = useMemo(() => {
    if (!searchText.trim()) return reports;
    const q = normalizeTurkish(searchText);
    return reports.filter(r =>
      normalizeTurkish(r.driver_name).includes(q) ||
      normalizeTurkish(r.license_plate).includes(q) ||
      normalizeTurkish(r.tank_card).includes(q)
    );
  }, [reports, searchText]);

  const totals = useMemo(() => {
    const totalLiters = filteredReports.reduce((s, r) => s + (r.liters || 0), 0);
    const totalCost = filteredReports.reduce((s, r) => s + (r.total_cost || 0), 0);
    return { liters: totalLiters, cost: totalCost, count: filteredReports.length };
  }, [filteredReports]);

  // Filtre değişince ilk sayfaya dön — kullanıcı 200 satır sonra arıyorsa
  // şaşırmasın diye.
  useEffect(() => { setPage(1); }, [searchText, reports.length]);

  const totalPages = Math.max(1, Math.ceil(filteredReports.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedReports = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredReports.slice(start, start + pageSize);
  }, [filteredReports, currentPage, pageSize]);

  const driverList = showAllDrivers ? allEmployees : employees;
  const firmEmployees = useMemo(() => {
    if (!formData.firm_id) return driverList;
    return driverList.filter(e => !e.user?.firm_id || e.user.firm_id === formData.firm_id);
  }, [driverList, formData.firm_id]);

  const displayDrivers = showAllDrivers ? driverList : firmEmployees;

  // Form handlers
  const openAdd = () => {
    setEditingId(null);
    setShowAllDrivers(false);
    setPendingFiles([]);
    setFormAttachments([]);
    setFormData({ ...emptyForm, firm_id: filterFirm && filterFirm !== 'all' ? filterFirm : '', date: new Date().toISOString().split('T')[0] });
    setShowForm(true);
  };

  const openEdit = (report) => {
    setEditingId(report.id);
    setShowAllDrivers(false);
    setPendingFiles([]);
    setFormAttachments([]);
    fetchFormAttachments(report.id);
    setFormData({
      firm_id: report.firm_id || '',
      date: report.date?.split('T')[0] || '',
      license_plate: report.license_plate || '',
      tank_card: report.tank_card || '',
      driver_name: report.driver_name || '',
      driver_id: report.driver_id || '',
      liters: report.liters?.toString() || '',
      km: report.km?.toString() || '',
      price_per_liter: report.price_per_liter?.toString() || '',
      total_cost: report.total_cost?.toString() || '',
      period: report.period || '',
      service_partner: report.service_partner || '',
      notes: report.notes || ''
    });
    setShowForm(true);
  };

  const handleClone = (report) => {
    setEditingId(null);
    setShowAllDrivers(false);
    setPendingFiles([]);
    setFormAttachments([]);
    setFormData({
      firm_id: report.firm_id || '',
      date: new Date().toISOString().split('T')[0],
      license_plate: report.license_plate || '',
      tank_card: report.tank_card || '',
      driver_name: report.driver_name || '',
      driver_id: report.driver_id || '',
      liters: report.liters?.toString() || '',
      km: report.km?.toString() || '',
      price_per_liter: report.price_per_liter?.toString() || '',
      total_cost: report.total_cost?.toString() || '',
      period: report.period || '',
      service_partner: report.service_partner || '',
      notes: report.notes || ''
    });
    setShowForm(true);
    toast.info(t('fuelReports.cloned') || 'Record cloned');
  };

  const handleSave = async () => {
    if (!formData.date || !formData.license_plate) {
      toast.error(t('fuelReports.dateAndPlateRequired'));
      return;
    }
    setSavingForm(true);
    try {
      const payload = {
        ...formData,
        liters: parseFloat(formData.liters) || 0,
        km: formData.km ? parseFloat(formData.km) : null,
        price_per_liter: formData.price_per_liter ? parseFloat(formData.price_per_liter) : null,
        total_cost: parseFloat(formData.total_cost) || 0,
      };
      const url = editingId ? `${API_URL}/api/fuel-reports/${editingId}` : `${API_URL}/api/fuel-reports`;
      const res = await fetch(url, { method: editingId ? 'PUT' : 'POST', headers, body: JSON.stringify(payload) });
      const data = await res.json();
      if (data.success) {
        // Upload pending files for new records
        const recordId = editingId || data.data?.id;
        if (pendingFiles.length > 0 && recordId) {
          await uploadPendingFiles(recordId);
        }
        toast.success(editingId ? t('fuelReports.updated') : t('fuelReports.added'));
        setShowForm(false);
        fetchReports();
        fetchPeriods();
      } else { toast.error(data.detail || t('fuelReports.errorOccurred')); }
    } catch { toast.error(t('fuelReports.operationFailed')); }
    finally { setSavingForm(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('fuelReports.deleteConfirm') || 'Are you sure?')) return;
    try {
      const res = await fetch(`${API_URL}/api/fuel-reports/${id}`, { method: 'DELETE', headers });
      const data = await res.json();
      if (data.success) { toast.success(t('fuelReports.deleted')); fetchReports(); }
    } catch { toast.error(t('fuelReports.cannotDelete')); }
  };

  const handleImport = async () => {
    if (!importFile || !importFirmId) { toast.error(t('fuelReports.fileAndFirmRequired')); return; }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      fd.append('firm_id', importFirmId);
      const res = await fetch(`${API_URL}/api/fuel-reports/import`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${accessToken}` }, body: fd
      });
      const data = await res.json();
      if (data.success) {
        toast.success(t('fuelReports.importSuccess', { count: data.imported_count }));
        setShowImport(false); setImportFile(null); fetchReports(); fetchPeriods();
      } else { toast.error(data.detail || t('fuelReports.errorOccurred')); }
    } catch { toast.error(t('fuelReports.importFailed')); }
    finally { setImporting(false); }
  };

  const updateFormField = (field, value) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'liters' || field === 'price_per_liter') {
        const l = parseFloat(field === 'liters' ? value : prev.liters) || 0;
        const p = parseFloat(field === 'price_per_liter' ? value : prev.price_per_liter) || 0;
        if (l > 0 && p > 0) next.total_cost = (l * p).toFixed(2);
      }
      return next;
    });
  };

  // Attachment handlers
  const openAttachments = async (reportId) => {
    setAttachReportId(reportId);
    setShowAttach(true);
    try {
      const res = await fetch(`${API_URL}/api/fuel-reports/${reportId}/attachments`, { headers });
      const data = await res.json();
      if (data.success) setAttachments(data.data || []);
    } catch {}
  };

  // ─── Ortak: Dosya Ekleri dialog'una verilen dosyaları yükle ────────────────
  // handleUploadAttachment (input change) ve handleAttachDrop bu helper'ı
  // kullanır; yeni paste (Ctrl+V) akışı da aynı yolu paylaşır → davranış birebir.
  const uploadAttachmentFiles = async (files) => {
    if (!files.length || !attachReportId) return;
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name}: Max 10MB`); continue; }
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await fetch(`${API_URL}/api/fuel-reports/${attachReportId}/attachments`, {
          method: 'POST', headers: { 'Authorization': `Bearer ${accessToken}` }, body: fd
        });
        const data = await res.json().catch(() => ({}));
        if (data.success !== false) {
          toast.success(`${file.name} ${t('fuelReports.fileUploaded') || 'yüklendi'}`);
        }
      } catch { toast.error(`${file.name} yüklenemedi`); }
      finally { setUploading(false); }
    }
    openAttachments(attachReportId);
    fetchReports();
  };

  const handleUploadAttachment = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !attachReportId) return;
    e.target.value = '';
    await uploadAttachmentFiles(files);
  };

  // Attachments dialog drag & drop
  const handleAttachDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setAttachDragging(true); };
  const handleAttachDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setAttachDragging(false); };
  const handleAttachDrop = async (e) => {
    e.preventDefault(); e.stopPropagation(); setAttachDragging(false);
    if (!attachReportId) return;
    const files = Array.from(e.dataTransfer.files);
    await uploadAttachmentFiles(files);
  };

  // ─── Ctrl+V (Paste) → Upload köprüsü ───────────────────────────────────────
  // Yakıt Kaydı Düzenle (showForm) açıkken pano dosyaları → handleFormFiles
  // Dosya Ekleri dialog'u (showAttach) açıkken pano dosyaları → uploadAttachmentFiles
  // accept filtresi mevcut dropzone ile birebir; metin yapıştırma etkilenmez.
  const PASTE_ACCEPT = 'image/*,.pdf,.doc,.docx,.xls,.xlsx';
  usePasteUpload((files) => handleFormFiles(files), { enabled: showForm, accept: PASTE_ACCEPT });
  usePasteUpload((files) => uploadAttachmentFiles(files), { enabled: showAttach && !!attachReportId, accept: PASTE_ACCEPT });

  // File preview handler
  const handlePreview = async (file, reportId) => {
    setPreviewFile(file);
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewError(null);
    setZoom(1);
    setRotation(0);
    setPdfPageNumber(1);
    setPdfNumPages(null);
    setPreviewUrl(null);

    // Pending file with local blob
    if (file.id?.toString().startsWith('pending_') && file.file) {
      const url = URL.createObjectURL(file.file);
      setPreviewUrl(url);
      setPreviewLoading(false);
      return;
    }

    const rid = reportId || attachReportId || editingId;
    if (!rid) { setPreviewError('Rapor bulunamadı'); setPreviewLoading(false); return; }

    try {
      const res = await fetch(`${API_URL}/api/fuel-reports/${rid}/attachments/${file.id}/download`, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch {
      setPreviewError('Önizleme yüklenemedi');
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (previewUrl && !previewFile?.id?.toString().startsWith('pending_')) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewOpen(false);
    setPreviewFile(null);
    setPreviewUrl(null);
    setPreviewError(null);
  };

  // File download handler
  const handleDownloadFile = async (file, reportId) => {
    if (file.id?.toString().startsWith('pending_') && file.file) {
      const url = URL.createObjectURL(file.file);
      const a = document.createElement('a');
      a.href = url; a.download = file.filename; a.click();
      URL.revokeObjectURL(url);
      return;
    }
    const rid = reportId || attachReportId || editingId;
    try {
      const res = await fetch(`${API_URL}/api/fuel-reports/${rid}/attachments/${file.id}/download`, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = file.filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('İndirme hatası'); }
  };

  const onPdfLoadSuccess = ({ numPages }) => setPdfNumPages(numPages);

  const handleDeleteAttachment = async (attId) => {
    if (!window.confirm(t('fuelReports.deleteConfirm') || 'Are you sure?')) return;
    try {
      const res = await fetch(`${API_URL}/api/fuel-reports/${attachReportId}/attachments/${attId}`, {
        method: 'DELETE', headers
      });
      const data = await res.json();
      if (data.success) { toast.success(t('fuelReports.fileDeleted')); openAttachments(attachReportId); fetchReports(); }
    } catch { toast.error(t('fuelReports.cannotDelete')); }
  };

  const exportCsv = () => {
    const header = [t('fuelReports.date') || 'Date', t('fuelReports.plate') || 'Plate', t('fuelReports.tankCard') || 'Tank Card', t('fuelReports.driver') || 'Driver', t('fuelReports.liters') || 'Liters', 'KM', '€/L', t('fuelReports.total') || 'Total', t('fuelReports.period') || 'Period', 'Service Partner'];
    const rows = filteredReports.map(r => [
      r.date, r.license_plate, r.tank_card, r.driver_name,
      r.liters, r.km || '', r.price_per_liter || '', r.total_cost, r.period, r.service_partner
    ]);
    const csv = [header, ...rows].map(r => r.join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `yakit_raporu_${new Date().toISOString().split('T')[0]}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const handleToggleAllDrivers = async () => {
    if (!showAllDrivers && allEmployees.length === 0) {
      await fetchAllEmployees();
    }
    setShowAllDrivers(!showAllDrivers);
  };

  // Drag & drop handlers for form attachments
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
  const handleFormDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    handleFormFiles(files);
  };
  const handleFormFileSelect = (e) => {
    const files = Array.from(e.target.files);
    handleFormFiles(files);
    e.target.value = '';
  };
  const handleFormFiles = async (files) => {
    const valid = files.filter(f => {
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name}: ${t('fuelReports.fileTooLarge')}`); return false; }
      return true;
    });
    if (!valid.length) return;

    if (editingId) {
      // Editing existing record: upload immediately
      for (const file of valid) {
        try {
          const fd = new FormData();
          fd.append('file', file);
          const res = await fetch(`${API_URL}/api/fuel-reports/${editingId}/attachments`, {
            method: 'POST', headers: { 'Authorization': `Bearer ${accessToken}` }, body: fd
          });
          const data = await res.json();
          if (data.success) {
            toast.success(`${file.name} ${t('fuelReports.fileUploaded').toLowerCase()}`);
            fetchFormAttachments(editingId);
          }
        } catch { toast.error(`${file.name} ${t('fuelReports.fileUploadFailed')}`); }
      }
    } else {
      // New record: queue files
      const newPending = valid.map(f => ({
        id: `pending_${Date.now()}_${Math.random()}`,
        file: f, filename: f.name, content_type: f.type,
        file_size: f.size, is_image: f.type.startsWith('image/'),
      }));
      setPendingFiles(prev => [...prev, ...newPending]);
    }
  };
  const removePendingFile = (id) => setPendingFiles(prev => prev.filter(f => f.id !== id));
  const removeFormAttachment = async (attId) => {
    if (!editingId) return;
    try {
      const res = await fetch(`${API_URL}/api/fuel-reports/${editingId}/attachments/${attId}`, { method: 'DELETE', headers });
      const data = await res.json();
      if (data.success) { toast.success(t('fuelReports.fileDeleted')); fetchFormAttachments(editingId); }
    } catch { toast.error(t('fuelReports.cannotDelete')); }
  };
  const fetchFormAttachments = async (reportId) => {
    try {
      const res = await fetch(`${API_URL}/api/fuel-reports/${reportId}/attachments`, { headers });
      const data = await res.json();
      if (data.success) setFormAttachments(data.data || []);
    } catch {}
  };
  const uploadPendingFiles = async (reportId) => {
    for (const pf of pendingFiles) {
      try {
        const fd = new FormData();
        fd.append('file', pf.file);
        await fetch(`${API_URL}/api/fuel-reports/${reportId}/attachments`, {
          method: 'POST', headers: { 'Authorization': `Bearer ${accessToken}` }, body: fd
        });
      } catch {}
    }
    setPendingFiles([]);
  };
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };
  const allFormFiles = [...formAttachments, ...pendingFiles];

  return (
    <div className="space-y-4 p-4 md:p-6" data-testid="fuel-reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
            <Fuel className="h-6 w-6 text-amber-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800 dark:text-white">
              {t('fuelReports.title') || 'Dagrapport Brandstof'}
            </h1>
            <p className="text-sm text-slate-500">{t('fuelReports.subtitle') || 'Yakıt Raporu'}</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={exportCsv} data-testid="fuel-export-btn">
            <Download className="h-4 w-4 mr-1" /> CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowImport(true)} data-testid="fuel-import-btn">
            <Upload className="h-4 w-4 mr-1" /> {t('fuelReports.excelImport') || 'Excel Import'}
          </Button>
          <Button size="sm" onClick={openAdd} data-testid="fuel-add-btn">
            <Plus className="h-4 w-4 mr-1" /> {t('fuelReports.add') || 'Yeni Ekle'}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-3">
        <Card><CardContent className="p-4 text-center">
          <div className="text-2xl font-bold text-amber-600">{totals.count}</div>
          <div className="text-xs text-slate-500">{t('fuelReports.totalRecords') || 'Kayıt'}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <div className="text-2xl font-bold text-blue-600">{totals.liters.toFixed(1)} L</div>
          <div className="text-xs text-slate-500">{t('fuelReports.totalLiters') || 'Toplam Litre'}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <div className="text-2xl font-bold text-emerald-600"><Money amount={totals.cost} /></div>
          <div className="text-xs text-slate-500">{t('fuelReports.totalCost') || 'Toplam Maliyet'}</div>
        </CardContent></Card>
      </div>

      {/* Filters */}
      <Card><CardContent className="p-3">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="w-40">
            <Label className="text-xs">{t('common.firm') || 'Firma'}</Label>
            <Select value={filterFirm} onValueChange={setFilterFirm}>
              <SelectTrigger className="h-9" data-testid="fuel-filter-firm"><SelectValue placeholder={t('vehicles.all')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('vehicles.all')}</SelectItem>
                {firms.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-40">
            <Label className="text-xs">{t('fuelReports.period') || 'Periyot'}</Label>
            <Select value={filterPeriod} onValueChange={setFilterPeriod}>
              <SelectTrigger className="h-9" data-testid="fuel-filter-period"><SelectValue placeholder={t('vehicles.all')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('vehicles.all')}</SelectItem>
                {periods.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-36">
            <Label className="text-xs">{t('fuelReports.startDate') || 'Başlangıç'}</Label>
            <Input type="date" className="h-9" value={filterDateStart} onChange={e => setFilterDateStart(e.target.value)} data-testid="fuel-filter-start-date" />
          </div>
          <div className="w-36">
            <Label className="text-xs">{t('fuelReports.endDate') || 'Bitiş'}</Label>
            <Input type="date" className="h-9" value={filterDateEnd} onChange={e => setFilterDateEnd(e.target.value)} data-testid="fuel-filter-end-date" />
          </div>
          <div className="flex-1 min-w-[180px]">
            <Label className="text-xs">{t('common.search') || 'Ara'}</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-2 h-4 w-4 text-slate-400" />
              <Input className="h-9 pl-8" placeholder={t('fuelReports.searchPlaceholder') || 'Şoför, plaka veya tank kart ara...'} value={searchText} onChange={e => setSearchText(e.target.value)} data-testid="fuel-search-input" />
              {searchText && <button onClick={() => setSearchText('')} className="absolute right-2 top-2"><X className="h-4 w-4 text-slate-400" /></button>}
            </div>
          </div>
        </div>
      </CardContent></Card>

      {/* Table */}
      <Card><CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">{t('fuelReports.date') || 'Tarih'}</TableHead>
                <TableHead>{t('fuelReports.plate') || 'Plaka'}</TableHead>
                <TableHead>{t('fuelReports.tankCard') || 'Tank Kart'}</TableHead>
                <TableHead>{t('fuelReports.driver') || 'Şoför'}</TableHead>
                <TableHead className="text-right">{t('fuelReports.liters') || 'Litre'}</TableHead>
                <TableHead className="text-right">KM</TableHead>
                <TableHead className="text-right">{t('fuelReports.pricePerLiter') || '€/L'}</TableHead>
                <TableHead className="text-right">{t('fuelReports.total') || 'Toplam'}</TableHead>
                <TableHead>{t('fuelReports.period') || 'Periyot'}</TableHead>
                <TableHead className="w-32 text-center">{t('common.actions') || 'İşlemler'}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={10} className="text-center py-8 text-slate-400">{t('common.loading')}</TableCell></TableRow>
              ) : filteredReports.length === 0 ? (
                <TableRow><TableCell colSpan={10} className="text-center py-8 text-slate-400">{t('fuelReports.noRecords') || 'No records found'}</TableCell></TableRow>
              ) : (
                pagedReports.map(r => (
                  <TableRow key={r.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <TableCell className="font-mono text-sm">{r.date}</TableCell>
                    <TableCell><Badge variant="outline" className="font-mono">{r.license_plate}</Badge></TableCell>
                    <TableCell className="text-sm text-slate-600 dark:text-slate-300">{r.tank_card || '-'}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-medium">{r.driver_name || '-'}</span>
                        {r.driver_id && <span className="w-2 h-2 rounded-full bg-emerald-400 shrink-0" title={t('fuelReports.matched') || 'Matched'} />}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-medium">{r.liters?.toFixed(1)}</TableCell>
                    <TableCell className="text-right text-slate-600 dark:text-slate-300">{r.km || '-'}</TableCell>
                    <TableCell className="text-right text-slate-600 dark:text-slate-300">{r.price_per_liter ? <Money amount={r.price_per_liter} revealKey={`fuel-ppl-${r.id}`} /> : '-'}</TableCell>
                    <TableCell className="text-right font-semibold text-emerald-600"><Money amount={r.total_cost} revealKey={`fuel-total-${r.id}`} /></TableCell>
                    <TableCell><Badge variant="secondary" className="text-xs">{r.period || '-'}</Badge></TableCell>
                    <TableCell>
                      <div className="flex gap-0.5 justify-center">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(r)} title={t('common.edit')} data-testid={`fuel-edit-${r.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 relative" onClick={() => openAttachments(r.id)} title="Dosya Ekleri" data-testid={`fuel-attach-${r.id}`}>
                          <Paperclip className="h-3.5 w-3.5" />
                          {r.attachment_count > 0 && (
                            <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">
                              {r.attachment_count}
                            </span>
                          )}
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-500" onClick={() => handleClone(r)} title="Kopyala" data-testid={`fuel-clone-${r.id}`}>
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700" onClick={() => handleDelete(r.id)} title="Sil" data-testid={`fuel-delete-${r.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination footer — sadece veri varsa render et. Sayfa boyutu
            seçici localStorage'a yazılır, kullanıcı tercihi korunur. */}
        {filteredReports.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-slate-100 dark:border-slate-800 text-xs" data-testid="fuel-pagination">
            <div className="text-slate-500 dark:text-slate-400">
              {((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, filteredReports.length)} / <span className="font-semibold text-slate-700 dark:text-slate-200">{filteredReports.length}</span> kayıt
            </div>
            <div className="flex items-center gap-2">
              <span className="text-slate-500 dark:text-slate-400">Sayfa başına:</span>
              <select
                className="h-7 px-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs"
                value={pageSize}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  setPageSize(v);
                  localStorage.setItem('fuel_reports_page_size', String(v));
                  setPage(1);
                }}
                data-testid="fuel-pagination-pagesize"
              >
                {[25, 50, 100, 200].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <div className="flex items-center gap-1 ml-2">
                <Button
                  size="icon"
                  variant="outline"
                  className="h-7 w-7"
                  disabled={currentPage <= 1}
                  onClick={() => setPage(1)}
                  title="İlk sayfa"
                  data-testid="fuel-pagination-first"
                >«</Button>
                <Button
                  size="icon"
                  variant="outline"
                  className="h-7 w-7"
                  disabled={currentPage <= 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  title="Önceki"
                  data-testid="fuel-pagination-prev"
                >‹</Button>
                <span className="mx-1 text-slate-600 dark:text-slate-300 tabular-nums">
                  <span className="font-semibold">{currentPage}</span> / {totalPages}
                </span>
                <Button
                  size="icon"
                  variant="outline"
                  className="h-7 w-7"
                  disabled={currentPage >= totalPages}
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  title="Sonraki"
                  data-testid="fuel-pagination-next"
                >›</Button>
                <Button
                  size="icon"
                  variant="outline"
                  className="h-7 w-7"
                  disabled={currentPage >= totalPages}
                  onClick={() => setPage(totalPages)}
                  title="Son sayfa"
                  data-testid="fuel-pagination-last"
                >»</Button>
              </div>
            </div>
          </div>
        )}
      </CardContent></Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Fuel className="h-5 w-5 text-amber-500" />
              {editingId ? (t('fuelReports.edit') || 'Yakıt Kaydı Düzenle') : (t('fuelReports.add') || 'Yeni Yakıt Kaydı')}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            {/* Firma */}
            <div>
              <Label className="text-xs">{t('common.firm') || 'Firma'} *</Label>
              <Select value={formData.firm_id} onValueChange={v => updateFormField('firm_id', v)}>
                <SelectTrigger className="h-9" data-testid="fuel-form-firm"><SelectValue placeholder={t('common.selectFirm')} /></SelectTrigger>
                <SelectContent>
                  {firms.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {/* Tarih */}
            <div>
              <Label className="text-xs">{t('fuelReports.date') || 'Tarih'} *</Label>
              <Input type="date" className="h-9" value={formData.date} onChange={e => updateFormField('date', e.target.value)} data-testid="fuel-form-date" />
            </div>
            {/* Plaka - Aranabilir Combobox */}
            <div>
              <Label className="text-xs">{t('fuelReports.plate') || 'Plaka'} *</Label>
              <Popover open={plateComboOpen} onOpenChange={setPlateComboOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" aria-expanded={plateComboOpen}
                    className="w-full justify-between font-normal h-9" data-testid="fuel-form-plate">
                    {formData.license_plate || (t('fuelReports.selectPlate') || 'Plaka seçin')}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder={t('fuelReports.searchPlate') || 'Plaka ara...'} />
                    <CommandList>
                      <CommandEmpty>{t('fuelReports.plateNotFound') || 'Plaka bulunamadı'}</CommandEmpty>
                      <CommandGroup>
                        {vehicles.map(v => (
                          <CommandItem
                            key={v.id}
                            value={`${v.license_plate} ${v.brand || ''} ${v.model || ''}`}
                            onSelect={() => {
                              updateFormField('license_plate', v.license_plate);
                              setPlateComboOpen(false);
                            }}
                          >
                            <Check className={`mr-2 h-4 w-4 ${formData.license_plate === v.license_plate ? "opacity-100" : "opacity-0"}`} />
                            <div className="flex flex-col">
                              <span className="font-mono font-medium">{v.license_plate}</span>
                              {(v.brand || v.model) && <span className="text-xs text-slate-500">{v.brand} {v.model}</span>}
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <Input className="h-8 mt-1 text-xs" placeholder={t('fuelReports.manualEntry') || 'veya manuel girin'} value={formData.license_plate} onChange={e => updateFormField('license_plate', e.target.value.toUpperCase())} />
            </div>
            {/* Tank Kart */}
            <div>
              <Label className="text-xs">{t('fuelReports.tankCard') || 'Tank Kart'}</Label>
              <Input className="h-9" value={formData.tank_card} onChange={e => updateFormField('tank_card', e.target.value)} data-testid="fuel-form-tank" />
            </div>

            {/* Şoför - Route form gibi */}
            <div className="col-span-2">
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs">{t('fuelReports.driver') || 'Şoför'}</Label>
                <Button
                  type="button"
                  variant={showAllDrivers ? "default" : "outline"}
                  size="sm"
                  className="h-6 text-xs px-2"
                  onClick={handleToggleAllDrivers}
                  disabled={loadingAll}
                  data-testid="fuel-toggle-all-drivers"
                >
                  {loadingAll ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                  {showAllDrivers ? (t('routes.form.firmDrivers') || 'Firma Şoförleri') : (t('routes.form.allDrivers') || 'Tüm Şoförler')}
                </Button>
              </div>
              <Popover open={driverComboOpen} onOpenChange={setDriverComboOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" aria-expanded={driverComboOpen}
                    className="w-full justify-between font-normal h-9"
                    disabled={!formData.firm_id && !showAllDrivers}
                  >
                    {formData.driver_id
                      ? displayDrivers.find(e => e.user?.id === formData.driver_id)?.user?.full_name || formData.driver_name || t('fuelReports.selectDriver')
                      : (formData.firm_id || showAllDrivers) ? t('fuelReports.searchDriver') : t('fuelReports.selectFirmFirst')}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder={t('fuelReports.searchDriverPlaceholder') || 'Search driver...'} />
                    <CommandList>
                      <CommandEmpty>{t('fuelReports.driverNotFound') || 'Driver not found'}</CommandEmpty>
                      <CommandGroup heading={showAllDrivers ? (t('routes.form.allDrivers') || 'All Drivers') : undefined}>
                        {displayDrivers.map(emp => {
                          const driverName = emp.user?.full_name || `${emp.voornaam || ''} ${emp.naam || ''}`.trim();
                          const firmName = firms.find(f => f.id === emp.user?.firm_id)?.name || '';
                          return (
                            <CommandItem
                              key={emp.user?.id}
                              value={`${driverName} ${firmName}`}
                              onSelect={() => {
                                setFormData(prev => ({ ...prev, driver_id: emp.user?.id, driver_name: driverName }));
                                setDriverComboOpen(false);
                              }}
                            >
                              <Check className={`mr-2 h-4 w-4 ${formData.driver_id === emp.user?.id ? "opacity-100" : "opacity-0"}`} />
                              <div className="flex flex-col">
                                <span>{driverName}</span>
                                {showAllDrivers && firmName && <span className="text-xs text-blue-500">{firmName}</span>}
                              </div>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <Input className="h-8 mt-1 text-xs" placeholder={t('fuelReports.manualDriverEntry') || 'or enter driver name'} value={formData.driver_name}
                onChange={e => setFormData(prev => ({ ...prev, driver_name: e.target.value, driver_id: '' }))} />
            </div>

            {/* Litre, KM, €/L, Toplam */}
            <div>
              <Label className="text-xs">{t('fuelReports.liters') || 'Litre Diesel'}</Label>
              <Input type="number" step="0.1" className="h-9" value={formData.liters} onChange={e => updateFormField('liters', e.target.value)} data-testid="fuel-form-liters" />
            </div>
            <div>
              <Label className="text-xs">KM</Label>
              <Input type="number" className="h-9" value={formData.km} onChange={e => updateFormField('km', e.target.value)} data-testid="fuel-form-km" />
            </div>
            <div>
              <Label className="text-xs">{t('fuelReports.pricePerLiter') || '€/Litre'}</Label>
              <Input type="number" step="0.001" className="h-9" value={formData.price_per_liter} onChange={e => updateFormField('price_per_liter', e.target.value)} data-testid="fuel-form-ppl" />
            </div>
            <div>
              <Label className="text-xs">{t('fuelReports.total') || 'Toplam (€)'}</Label>
              <Input type="number" step="0.01" className="h-9" value={formData.total_cost} onChange={e => updateFormField('total_cost', e.target.value)} data-testid="fuel-form-total" />
            </div>
            <div>
              <Label className="text-xs">{t('fuelReports.period') || 'Periyot'}</Label>
              <Input className="h-9" placeholder="2025/4" value={formData.period} onChange={e => updateFormField('period', e.target.value)} data-testid="fuel-form-period" />
            </div>
            <div>
              <Label className="text-xs">Service Partner</Label>
              <Input className="h-9" value={formData.service_partner} onChange={e => updateFormField('service_partner', e.target.value)} data-testid="fuel-form-sp" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">{t('fuelReports.notes') || 'Notlar'}</Label>
              <Input className="h-9" value={formData.notes} onChange={e => updateFormField('notes', e.target.value)} data-testid="fuel-form-notes" />
            </div>

            {/* Dosya Ekleri - Sürükle Bırak */}
            <div className="col-span-2 pt-2 border-t">
              <div className="flex items-center gap-2 mb-2">
                <Paperclip className="h-4 w-4 text-slate-500" />
                <span className="text-xs font-medium">{t('fuelReports.attachments') || 'Dosya Ekleri'}</span>
                {allFormFiles.length > 0 && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{allFormFiles.length}</span>
                )}
              </div>
              <div
                className={`border-2 border-dashed rounded-lg p-3 text-center transition-all cursor-pointer ${
                  isDragging ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-slate-200 dark:border-slate-700 hover:border-blue-300'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleFormDrop}
                onClick={() => document.getElementById('fuel-form-file-input').click()}
                data-testid="fuel-form-dropzone"
              >
                <input id="fuel-form-file-input" type="file" className="hidden" onChange={handleFormFileSelect} multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx" />
                <Upload className={`h-5 w-5 mx-auto mb-1 ${isDragging ? 'text-blue-500' : 'text-slate-400'}`} />
                <p className="text-xs text-slate-500">{t('fuelReports.dragDropFiles') || 'Drag files or click'} · <span className="font-medium text-blue-600 dark:text-blue-400">Ctrl+V</span> ile yapıştır</p>
                <p className="text-xs text-slate-400">Resim, PDF, Word, Excel (max 10MB)</p>
              </div>
              {allFormFiles.length > 0 && (
                <div className="space-y-1.5 mt-2 max-h-40 overflow-y-auto">
                  {allFormFiles.map(file => (
                    <div key={file.id} className={`flex items-center gap-2 p-2 rounded-lg border ${
                      file.id.toString().startsWith('pending_') ? 'bg-amber-50 border-amber-200 dark:bg-amber-900/20' : 'bg-slate-50 border-slate-200 dark:bg-slate-800'
                    }`}>
                      <div className="w-8 h-8 flex-shrink-0 rounded overflow-hidden bg-white dark:bg-slate-700 flex items-center justify-center">
                        {file.is_image ? <ImageIcon className="h-4 w-4 text-blue-500" /> : <FileText className="h-4 w-4 text-slate-500" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate">{file.filename}</p>
                        <p className="text-xs text-slate-400">
                          {formatFileSize(file.file_size)}
                          {file.id.toString().startsWith('pending_') && <span className="ml-1 text-amber-600">Bekliyor</span>}
                        </p>
                      </div>
                      <div className="flex items-center gap-0.5">
                        {isPreviewable(file) && (
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={(e) => { e.stopPropagation(); handlePreview(file, editingId); }} title="Önizle">
                            <Eye className="h-3 w-3" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={(e) => { e.stopPropagation(); handleDownloadFile(file, editingId); }} title="İndir">
                          <Download className="h-3 w-3" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500 hover:text-red-700" onClick={(e) => {
                          e.stopPropagation();
                          file.id.toString().startsWith('pending_') ? removePendingFile(file.id) : removeFormAttachment(file.id);
                        }}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Hesaplanan */}
            {(parseFloat(formData.liters) > 0 && parseFloat(formData.price_per_liter) > 0) && (
              <div className="col-span-2 bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg text-sm">
                <span className="text-slate-500">Hesaplanan:</span>
                <span className="font-bold text-amber-700 dark:text-amber-400 ml-2">
                  {parseFloat(formData.liters).toFixed(1)} L x €{parseFloat(formData.price_per_liter).toFixed(3)} = {formatCurrency(parseFloat(formData.liters) * parseFloat(formData.price_per_liter))}
                </span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSave} disabled={savingForm} data-testid="fuel-form-save">
              {savingForm ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
              {editingId ? t('common.save') : t('common.add')}
              {pendingFiles.length > 0 && ` (${pendingFiles.length} dosya)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-emerald-500" />
              {t('fuelReports.importTitle') || 'Excel Dosyasından Aktar'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>{t('common.firm') || 'Firma'} *</Label>
              <Select value={importFirmId} onValueChange={setImportFirmId}>
                <SelectTrigger data-testid="fuel-import-firm"><SelectValue placeholder={t('common.selectFirm')} /></SelectTrigger>
                <SelectContent>{firms.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Excel (.xlsx) *</Label>
              <label className="flex items-center justify-center w-full h-24 border-2 border-dashed rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 mt-1">
                <div className="text-center">
                  {importFile ? (
                    <div className="flex items-center gap-2 text-emerald-600"><FileSpreadsheet className="h-5 w-5" /><span className="text-sm font-medium">{importFile.name}</span></div>
                  ) : (
                    <div className="text-slate-400"><Upload className="h-6 w-6 mx-auto mb-1" /><span className="text-xs">{t('fuelReports.dragDropFiles') || 'Dosyaları sürükleyin veya tıklayın'}</span></div>
                  )}
                </div>
                <input type="file" accept=".xlsx,.xls" className="hidden" onChange={e => setImportFile(e.target.files[0])} data-testid="fuel-import-file" />
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowImport(false); setImportFile(null); }}>{t('common.cancel')}</Button>
            <Button onClick={handleImport} disabled={importing || !importFile || !importFirmId} data-testid="fuel-import-submit">
              {importing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
              {importing ? (t('fuelReports.importing') || 'Aktarılıyor...') : (t('fuelReports.import') || 'Aktar')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Attachments Dialog */}
      <Dialog open={showAttach} onOpenChange={setShowAttach}>
        <DialogContent className="max-w-md w-full overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Paperclip className="h-5 w-5 text-blue-500" />
              {t('fuelReports.attachments') || 'Dosya Ekleri'}
              {attachments.length > 0 && <Badge variant="secondary">{attachments.length}</Badge>}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {/* Drop Zone */}
            <div
              className={`border-2 border-dashed rounded-lg p-4 text-center transition-all cursor-pointer ${
                attachDragging ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-slate-200 dark:border-slate-700 hover:border-blue-300'
              }`}
              onDragOver={handleAttachDragOver}
              onDragLeave={handleAttachDragLeave}
              onDrop={handleAttachDrop}
              onClick={() => document.getElementById('fuel-attach-file-input')?.click()}
              data-testid="fuel-attach-dropzone"
            >
              <input id="fuel-attach-file-input" type="file" className="hidden" onChange={handleUploadAttachment} multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx" data-testid="fuel-attach-upload" />
              {uploading ? (
                <Loader2 className="h-6 w-6 animate-spin mx-auto text-blue-500" />
              ) : (
                <Upload className={`h-6 w-6 mx-auto mb-1 ${attachDragging ? 'text-blue-500' : 'text-slate-400'}`} />
              )}
              <p className="text-xs text-slate-500">{t('fuelReports.dragDropFiles') || 'Dosyaları sürükleyin veya tıklayın'} · <span className="font-medium text-blue-600 dark:text-blue-400">Ctrl+V</span> ile yapıştır</p>
              <p className="text-xs text-slate-400 mt-0.5">Resim, PDF, Word, Excel (max 10MB)</p>
            </div>

            {/* File list */}
            {attachments.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">{t('fuelReports.noAttachments') || 'Dosya eki yok'}</p>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {attachments.map(att => (
                  <div key={att.id} className="flex items-start gap-2 p-2 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                    <div className="w-9 h-9 flex-shrink-0 rounded overflow-hidden bg-white dark:bg-slate-700 flex items-center justify-center">
                      {att.is_image ? <ImageIcon className="h-4 w-4 text-blue-500" /> : <FileText className="h-4 w-4 text-slate-500" />}
                    </div>
                    {/* Filename — uzun isimler artık ALT SATIRA İNER (truncate
                        yerine break-all). Modal yana doğru genişlemez. */}
                    <div className="flex-1 min-w-0">
                      <p
                        className="text-sm font-medium break-all leading-snug"
                        data-testid={`fuel-att-name-${att.id}`}
                      >{att.filename}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{(att.file_size / 1024).toFixed(1)} KB</p>
                    </div>
                    <div className="flex-shrink-0 flex items-center gap-0.5">
                      {isPreviewable(att) && (
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handlePreview(att, attachReportId)} title="Önizle" data-testid={`fuel-att-preview-${att.id}`}>
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleDownloadFile(att, attachReportId)} title="İndir" data-testid={`fuel-att-download-${att.id}`}>
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500 hover:text-red-600" onClick={() => handleDeleteAttachment(att.id)} title="Sil">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAttach(false)}>{t('common.close') || 'Kapat'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* File Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={closePreview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="fuel-preview-modal">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-lg font-semibold flex items-center gap-2">
              {isPdfFile(previewFile) ? <FileText className="h-5 w-5 text-red-500" /> : <ImageIcon className="h-5 w-5 text-blue-500" />}
              <span className="truncate max-w-md">{previewFile?.filename || 'Önizleme'}</span>
              {isPdfFile(previewFile) && pdfNumPages && <Badge variant="outline" className="ml-2">{pdfPageNumber} / {pdfNumPages}</Badge>}
            </DialogTitle>
          </DialogHeader>

          {/* Controls */}
          <div className="flex items-center justify-center gap-4 py-2 border-b flex-shrink-0">
            {isPdfFile(previewFile) && (
              <>
                <Button variant="outline" size="sm" onClick={() => setPdfPageNumber(p => Math.max(1, p - 1))} disabled={pdfPageNumber <= 1}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm">{pdfPageNumber} / {pdfNumPages || '?'}</span>
                <Button variant="outline" size="sm" onClick={() => setPdfPageNumber(p => Math.min(pdfNumPages || p, p + 1))} disabled={pdfPageNumber >= (pdfNumPages || 1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <div className="border-l pl-4 flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setZoom(s => Math.max(0.5, s - 0.25))}><ZoomOut className="h-4 w-4" /></Button>
                  <span className="text-sm w-16 text-center">{Math.round(zoom * 100)}%</span>
                  <Button variant="outline" size="sm" onClick={() => setZoom(s => Math.min(2, s + 0.25))}><ZoomIn className="h-4 w-4" /></Button>
                </div>
              </>
            )}
            {isImageFile(previewFile) && (
              <>
                <Button variant="outline" size="sm" onClick={() => setZoom(z => Math.max(0.25, z - 0.25))}><ZoomOut className="h-4 w-4" /></Button>
                <span className="text-sm w-16 text-center">{Math.round(zoom * 100)}%</span>
                <Button variant="outline" size="sm" onClick={() => setZoom(z => Math.min(3, z + 0.25))}><ZoomIn className="h-4 w-4" /></Button>
                <Button variant="outline" size="sm" onClick={() => setRotation(r => (r + 90) % 360)}><RotateCw className="h-4 w-4" /></Button>
              </>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto bg-slate-100 dark:bg-slate-900 rounded-lg flex items-start justify-center min-h-[400px] p-4">
            {previewLoading ? (
              <div className="flex flex-col items-center gap-3 py-20"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /><p className="text-sm text-slate-500">Yükleniyor...</p></div>
            ) : previewError ? (
              <div className="text-center text-red-500 py-20">
                <p>{previewError}</p>
                <Button className="mt-4" variant="outline" onClick={() => previewFile && handleDownloadFile(previewFile)}><Download className="h-4 w-4 mr-2" />Dosyayı İndir</Button>
              </div>
            ) : isImageFile(previewFile) && previewUrl ? (
              <div className="overflow-auto w-full h-full flex items-center justify-center">
                <img src={previewUrl} alt={previewFile?.filename || 'Preview'} className="max-w-full transition-transform duration-200" style={{ transform: `scale(${zoom}) rotate(${rotation}deg)`, transformOrigin: 'center center' }} />
              </div>
            ) : isPdfFile(previewFile) && previewUrl ? (
              <Document file={previewUrl} onLoadSuccess={onPdfLoadSuccess} onLoadError={() => setPreviewError('PDF yüklenemedi')}
                loading={<div className="flex items-center gap-2 py-20"><Loader2 className="h-6 w-6 animate-spin" /><span>PDF yükleniyor...</span></div>}
                error={<div className="text-center py-10 text-red-500"><FileText className="h-12 w-12 mx-auto mb-2 text-red-400" /><p>PDF yüklenemedi</p></div>}
              >
                <Page pageNumber={pdfPageNumber} scale={zoom} renderTextLayer={true} renderAnnotationLayer={true} />
              </Document>
            ) : (
              <div className="text-center p-8 py-20">
                <FileText className="h-12 w-12 mx-auto mb-4 text-slate-400" />
                <p className="text-slate-500 mb-4">Bu dosya türü önizlenemez</p>
                <Button onClick={() => previewFile && handleDownloadFile(previewFile)}><Download className="h-4 w-4 mr-2" />Dosyayı İndir</Button>
              </div>
            )}
          </div>

          {/* Footer */}
          <DialogFooter className="flex-shrink-0 border-t pt-4">
            <div className="flex justify-between items-center w-full">
              <span className="text-sm text-slate-500">{previewFile?.file_size ? `${(previewFile.file_size / 1024).toFixed(1)} KB` : ''}</span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={closePreview}>{t('common.close') || 'Kapat'}</Button>
                <Button onClick={() => previewFile && handleDownloadFile(previewFile)}><Download className="h-4 w-4 mr-2" />{t('common.download') || 'İndir'}</Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
